import React from 'react';
import { isUndefined } from 'lodash-es';

import {
  ConfiguratorContextEngagementPackage,
  ConfiguratorEngagementLayout,
  ConfiguratorEngagementPackage,
  ConfiguratorEngagementPackageBackClickHandler,
  ConfiguratorEngagementPackageClickHandler,
  ConfiguratorEngagementPackageLevelClickHandler,
  Maybe,
} from '../types';
import {
  getEngagementDefaultSelection,
  getEngagementPackage,
  getEngagementPackageLevelDetails,
  getEngagementPackageLevels,
} from '../utils/deprecated';

export function useEngagementStep(configEngagementPackages: ConfiguratorEngagementPackage[]) {
  const [pack, setPack] = React.useState<Maybe<ConfiguratorEngagementPackage>>();
  const [packages, setPackages] = React.useState<ConfiguratorEngagementPackage[]>([]);
  const [selection, setSelection] = React.useState<Record<string, string | false>>({});

  const [layout, setLayout] = React.useState<ConfiguratorEngagementLayout>('default');
  const [isPackageOpen, setIsPackageOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    setPackages(configEngagementPackages);
    setSelection(getEngagementDefaultSelection(configEngagementPackages));
  }, [configEngagementPackages]);

  const handleEngagementPackageBackClick = React.useCallback<ConfiguratorEngagementPackageBackClickHandler>(() => {
    setLayout('default');
    setIsPackageOpen(false);
  }, []);

  const handleEngagementPackageClick = React.useCallback<ConfiguratorEngagementPackageClickHandler>(
    (nextPackageId) => {
      setPack(getEngagementPackage(packages, nextPackageId));
      setLayout('details');
      setIsPackageOpen(true);
    },
    [packages],
  );

  const handleEngagementPackageLevelClick = React.useCallback<ConfiguratorEngagementPackageLevelClickHandler>(
    (nextLevelId, packageId) => {
      setSelection((prevState) => ({
        ...prevState,
        [packageId]: nextLevelId,
      }));
    },
    [],
  );

  const engagementPackage = React.useMemo<Maybe<ConfiguratorContextEngagementPackage>>(() => {
    if (isUndefined(pack)) {
      return;
    }

    return {
      ...pack,
      active: true,
      isOpen: true,
      onBackClick: handleEngagementPackageBackClick,
      onClick: () => handleEngagementPackageClick(pack.id),
      level: getEngagementPackageLevelDetails(pack.levels, selection[pack.id]),
      levels: getEngagementPackageLevels(pack.levels).map((level) => ({
        ...level,
        active: level.id === selection[pack.id],
        onClick: () => handleEngagementPackageLevelClick(level.id, pack.id),
      })),
    };
  }, [
    pack,
    selection,
    handleEngagementPackageBackClick,
    handleEngagementPackageClick,
    handleEngagementPackageLevelClick,
  ]);

  const engagementPackages = React.useMemo<ConfiguratorContextEngagementPackage[]>(() => {
    return packages.map((engagementPackage) => ({
      ...engagementPackage,
      active: engagementPackage.id === pack?.id,
      isOpen: isPackageOpen && engagementPackage.id === pack?.id,
      onBackClick: handleEngagementPackageBackClick,
      onClick: () => handleEngagementPackageClick(engagementPackage.id),
      level: getEngagementPackageLevelDetails(engagementPackage.levels, selection[engagementPackage.id]),
      levels: getEngagementPackageLevels(engagementPackage.levels).map((level) => ({
        ...level,
        active: level.id === selection[engagementPackage.id],
        onClick: () => handleEngagementPackageLevelClick(level.id, engagementPackage.id),
      })),
    }));
  }, [
    pack,
    packages,
    selection,
    isPackageOpen,
    handleEngagementPackageBackClick,
    handleEngagementPackageClick,
    handleEngagementPackageLevelClick,
  ]);

  return React.useMemo(
    () => ({
      engagementPackage,
      engagementPackages,
      engagementLayout: layout,
    }),
    [engagementPackage, engagementPackages, layout],
  );
}
